import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared';
import { SettingsService } from '../../..';

type ActionKey = 'Copy' | 'BulkCopy' | 'Montage' | 'BulkMontage';

interface ActionFlag {
  key: ActionKey;
  label: string;
  enabled: boolean;
}

interface ConfigRow {
  label: string;
  value: string;
}

interface ProviderVM {
  raw: any;
  id: string;
  name: string;
  typeLabel: string;
  providerType: number;
  /** JAI providers always poll the batch to completion, so the batch-strategy toggle is hidden for them. */
  isJai: boolean;
  isEnabled: boolean;
  /** Bulk-copy batch strategy: true = poll the provider batch to completion; false = save pending items and poll each offer individually. */
  pollBatch: boolean;
  actions: ActionFlag[];
  configRows: ConfigRow[];
  /** false when Config JSON couldn't be parsed — saving is blocked so we never wipe it. */
  configParsed: boolean;
  dirty: boolean;
  saving: boolean;
}

const ACTION_LABELS: { key: ActionKey; label: string }[] = [
  { key: 'Copy', label: 'Copy' },
  { key: 'BulkCopy', label: 'Bulk Copy' },
  { key: 'Montage', label: 'Montage' },
  { key: 'BulkMontage', label: 'Bulk Montage' },
];

// Backend IAProviderType ordinals.
const TYPE_LABELS: { [n: number]: string } = {
  0: 'Junction AI',
  1: 'CEIA · Fine-Tuned',
  2: 'CEIA · Frontier',
  3: 'CEIA · Local',
};

@Component({
  selector: 'app-manage-providers-dialog',
  templateUrl: './manage-providers-dialog.component.html',
  styleUrls: ['./manage-providers-dialog.component.scss'],
})
export class ManageProvidersDialogComponent implements OnInit {
  providers: ProviderVM[] = [];
  loading = true;
  loadError = false;
  changed = false;

  constructor(
    private settingsService: SettingsService,
    private snack: MatSnackBar,
    private dialogRef: MatDialogRef<ManageProvidersDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.settingsService.getIAProviders().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.value ?? []);
        this.providers = list.map((p: any) => this.toVm(p));
        this.loading = false;
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  // ------------------------------------------------------------------
  // View model (tolerant of OData PascalCase or camelCase responses)
  // ------------------------------------------------------------------

  private toVm(p: any): ProviderVM {
    // ProviderType arrives as an OData enum NAME ("JAI"/"CEIA_Frontier"/...) or an ordinal.
    const { ordinal, isJai, label } = this.parseProviderType(this.pick(p, 'ProviderType', 'providerType'));
    const rawConfig = this.pick(p, 'Config', 'config');
    const parsed = this.tryParse(rawConfig);
    const config = parsed.value;

    // SupportedActions is a first-class vertex property (OData serializes the enum array as
    // name strings). Reflect only what's stored — a provider with none shows every action OFF.
    const supported = this.pick(p, 'SupportedActions', 'supportedActions');
    const configured = (Array.isArray(supported) ? supported.map((x: any) => String(x)) : []).map((a) => a.toLowerCase());

    return {
      raw: p,
      id: this.pick(p, 'Id', 'id'),
      name: this.pick(p, 'ProviderName', 'providerName') ?? '(unnamed)',
      typeLabel: label,
      providerType: ordinal,
      isJai,
      isEnabled: !!this.pick(p, 'IsEnabled', 'isEnabled'),
      // First-class vertex property; defaults to true (poll) when absent on older provider records.
      pollBatch: this.pick(p, 'PollBulkBatchToCompletion', 'pollBulkBatchToCompletion') ?? true,
      actions: ACTION_LABELS.map((a) => ({ ...a, enabled: configured.includes(a.key.toLowerCase()) })),
      configRows: this.buildConfigRows(isJai, config),
      configParsed: parsed.ok,
      dirty: false,
      saving: false,
    };
  }

  /** Resolves ProviderType from an OData enum name or a numeric ordinal. */
  private parseProviderType(raw: any): { ordinal: number; isJai: boolean; label: string } {
    if (typeof raw === 'number') {
      return { ordinal: raw, isJai: raw === 0, label: TYPE_LABELS[raw] ?? `Type ${raw}` };
    }
    const s = String(raw ?? '').trim();
    const byName: { [k: string]: number } = { jai: 0, ceia_finetuned: 1, ceia_frontier: 2, ceia_local: 3 };
    const ord = byName[s.toLowerCase()];
    if (ord !== undefined) { return { ordinal: ord, isJai: ord === 0, label: TYPE_LABELS[ord] }; }
    return { ordinal: -1, isJai: false, label: s || 'Unknown' };
  }

  private buildConfigRows(isJai: boolean, config: any): ConfigRow[] {
    const rows: ConfigRow[] = [];
    const add = (label: string, value: any, secret = false) =>
      rows.push({ label, value: this.display(value, secret) });

    if (isJai) {
      add('Endpoint', this.cfgGet(config, 'Endpoint'));
      add('Organization Id', this.cfgGet(config, 'OrganizationId'));
      add('Client Secret', this.cfgGet(config, 'ClientSecret'), true);
      add('Template Id', this.cfgGet(config, 'TemplateId'));
      add('Lookup Batch Id', this.cfgGet(config, 'LookupBatchId'));
      add('Automation Name', this.cfgGet(config, 'AutomationName'));
    } else {
      add('Base URL', this.cfgGet(config, 'BaseUrl', 'Baseurl'));
      add('Client Id', this.cfgGet(config, 'ClientId'));
      add('API Key', this.cfgGet(config, 'ApiKey'), true);
    }
    return rows;
  }

  // ------------------------------------------------------------------
  // Edits
  // ------------------------------------------------------------------

  toggleEnabled(p: ProviderVM, checked: boolean): void {
    p.isEnabled = checked;
    p.dirty = true;
  }

  toggleAction(p: ProviderVM, action: ActionFlag, checked: boolean): void {
    action.enabled = checked;
    p.dirty = true;
  }

  togglePollBatch(p: ProviderVM, checked: boolean): void {
    p.pollBatch = checked;
    p.dirty = true;
  }

  canSave(p: ProviderVM): boolean {
    return p.dirty && !p.saving;
  }

  save(p: ProviderVM): void {
    p.saving = true;

    // SupportedActions is its own vertex property (enum names). Config is left untouched —
    // spread the original vertex so nothing else is dropped; only IsEnabled + SupportedActions change.
    const supportedActions = p.actions.filter((a) => a.enabled).map((a) => a.key);
    const payload = { ...p.raw, IsEnabled: p.isEnabled, SupportedActions: supportedActions, PollBulkBatchToCompletion: p.pollBatch };

    this.settingsService.updateIAProvider(payload, p.id).subscribe({
      next: (saved: any) => {
        p.raw = saved ?? payload;
        p.dirty = false;
        p.saving = false;
        this.changed = true;
        this.snack.open(`${p.name}: saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
      },
      error: (err: any) => {
        p.saving = false;
        this.snack.open(`${p.name}: ${this.errorText(err)}`, 'OK',
          { verticalPosition: 'top', panelClass: ['snackbar-error'] });
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  private pick(obj: any, ...keys: string[]): any {
    if (!obj) { return undefined; }
    for (const k of keys) { if (obj[k] !== undefined) { return obj[k]; } }
    return undefined;
  }

  private tryParse(raw: any): { ok: boolean; value: any } {
    if (raw == null || raw === '') { return { ok: true, value: {} }; }
    if (typeof raw === 'object') { return { ok: true, value: { ...raw } }; }
    try { return { ok: true, value: JSON.parse(raw) || {} }; }
    catch { return { ok: false, value: {} }; }
  }

  private cfgGet(config: any, ...keys: string[]): any {
    if (!config) { return undefined; }
    const lowerKeys = Object.keys(config).reduce((m: any, c) => { m[c.toLowerCase()] = c; return m; }, {});
    for (const k of keys) {
      const actual = lowerKeys[k.toLowerCase()];
      if (actual && config[actual] != null && config[actual] !== '') { return config[actual]; }
    }
    return undefined;
  }

  private display(value: any, secret: boolean): string {
    if (value == null || value === '') { return '—'; }
    if (secret) { return '••••••••'; }
    return String(value);
  }

  private errorText(err: any): string {
    if (Array.isArray(err?.error)) {
      return err.error.map((e: any) => e.ErrorMessage ?? e.errorMessage ?? e).join(' ');
    }
    return err?.error?.message || err?.error || err?.message || 'Save failed';
  }
}
